import { useState } from "react";
import { useOrgSummary } from "hooks/useOrgSummary";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "layouts/pages/LandingPage/components/Footer";
import EventTable from "components/EventTable";
import USLocationMap from "components/USLocationMap";
import AllSensorStatusCard from "components/AllSensorStatusCard";
import DataTable from "examples/Tables/DataTable";
import TopNavbar from "examples/Navbars/TopNavbar";

const OrganizationOverview: React.FC = () => {
    const { orgData, isLoading, error, markers } = useOrgSummary();
    const [hoveredLocation, setHoveredLocation] = useState<string | null>(null);

    if (isLoading) return <div>Loading...</div>;
    if (error) return <div>Error: {error.message}</div>;
    if (!orgData || orgData.length === 0) return <div>No organization data available</div>;

    const sensorStatuses = orgData.map((org) => org.sensor_status);
    const totalLocations = orgData.length;
    const totalEvents = orgData.reduce((acc, org) => acc + org.total_events, 0);

    const columns = [
        { Header: "Location", accessor: "location_name" },
        { Header: "Trigger Level", accessor: "trigger_level" },
        { Header: "Max SPL (30 days)", accessor: "max_spl" },
        { Header: "Events (30 days)", accessor: "total_events" },
        { Header: "Status", accessor: "sensor_status" },
    ];

    // Add highlighting to the table data based on the hovered location
    const data = orgData.map((org) => ({
        id: org.id,
        location_name: org.location_name,
        trigger_level: org.trigger_level,
        max_spl: org.max_spl,
        total_events: org.total_events,
        sensor_status: org.sensor_status,
        highlighted: hoveredLocation === org.location_name, // NEW: Highlight condition
    }));

    return (
        <DashboardLayout>
            <TopNavbar />
            <MDBox mt={4} mb={4} mx={3}>
                {/* First Row */}
                <Grid container spacing={2}>
                    {/* First Column (4-wide) */}
                    <Grid item xs={12} md={4} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Card sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <MDTypography variant="h2" align="center">[solidcore]</MDTypography>
                        </Card>
                        <AllSensorStatusCard sensorStatuses={sensorStatuses} />
                        <Grid container spacing={2} sx={{ flex: 1 }}>
                            <Grid item xs={6}>
                                <Card sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 2 }}>
                                    <MDBox textAlign="center" sx={{ minHeight: 80, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                                        <MDTypography variant="subtitle2">Noise Monitors</MDTypography>
                                        <MDTypography variant="h2" fontWeight="bold">{totalLocations}</MDTypography>
                                        <MDTypography variant="caption" sx={{ visibility: "hidden" }}>Placeholder</MDTypography>
                                    </MDBox>
                                </Card>
                            </Grid>
                            <Grid item xs={6}>
                                <Card sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 2 }}>
                                    <MDBox textAlign="center" sx={{ minHeight: 80, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                                        <MDTypography variant="subtitle2">Noise Events</MDTypography>
                                        <MDTypography variant="h2" fontWeight="bold">{totalEvents}</MDTypography>
                                        <MDTypography variant="caption">Past 30 days</MDTypography>
                                    </MDBox>
                                </Card>
                            </Grid>
                        </Grid>
                    </Grid>
                    {/* Second Column (8-wide) */}
                    <Grid item xs={12} md={8}>
                        <USLocationMap markers={markers} onHover={setHoveredLocation} />
                    </Grid>
                </Grid>

                {/* Additional Rows */}
                <MDBox mt={4}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Card>
                                <MDBox p={2}>
                                    <MDTypography variant="h5">Summary by Location</MDTypography>
                                    <DataTable table={{ columns, rows: data }} entriesPerPage={false}/>
                                </MDBox>
                            </Card>
                        </Grid>

                    </Grid>
                </MDBox>
            </MDBox>
            <Footer />
        </DashboardLayout>
    );
};

export default OrganizationOverview;
